import { PERMISSIONS } from './permissions.models';

/**
 * Route to permission mapping
 * Maps routes to required permissions for access control
 */
export const ROUTE_PERMISSIONS: Record<string, string[]> = {
  // Box-related routes
  '/su_boxes': [PERMISSIONS.BoxView],
  '/cu_boxes': [PERMISSIONS.BoxView],
  '/su_orders': [PERMISSIONS.BoxView],
  '/cu_orders': [PERMISSIONS.BoxView],
  '/su_pboxes': [PERMISSIONS.BoxManage, PERMISSIONS.BoxAdmin],
  '/su_expiration': [PERMISSIONS.BoxView],
  '/cu_expiration': [PERMISSIONS.BoxView],
  '/su_alarms': [PERMISSIONS.BoxManage, PERMISSIONS.BoxAdmin],
  '/su_archive': [PERMISSIONS.BoxView],
  '/cu_archive': [PERMISSIONS.BoxView],
  '/su_devs': [PERMISSIONS.BoxAdmin],
  '/su_fw_builds': [PERMISSIONS.BoxAdmin],
  '/su_dashboard': [PERMISSIONS.BoxView],
  '/dashboard': [PERMISSIONS.BoxView],

  // IoBox Light routes
  '/su_light_boxes': [PERMISSIONS.LightView, PERMISSIONS.LightManage],
  '/su_light_batches': [PERMISSIONS.LightManage],
  '/su_light_customers': [PERMISSIONS.LightManage],
  '/su_light_devices': [PERMISSIONS.LightManage],

  // Spare Parts routes
  '/spare_parts_boxes': [PERMISSIONS.SparePartsView],
  '/spare_parts_expiration': [PERMISSIONS.SparePartsView],
  '/spare_parts_projects': [PERMISSIONS.SparePartsView],
  '/spare_parts_safety_stock': [PERMISSIONS.SparePartsView],
  '/spare_parts_product_list': [PERMISSIONS.SparePartsManage],

  // Pallets routes
  '/su_pallets': [PERMISSIONS.PalletsView, PERMISSIONS.PalletsManage],

  // User Management routes
  '/su_users': [PERMISSIONS.UserManage],

  // Pending routes (no specific permission required, handled by AuthGuard)
  '/pe_landing': []
};

/**
 * Get required permissions for a route
 * Supports wildcard patterns (e.g., '/su_light_*')
 */
export function getRequiredPermissions(route: string): string[] {
  // Normalize route (remove query params and fragments)
  const normalizedRoute = route.split('?')[0].split('#')[0];
  
  // Check exact match first
  if (ROUTE_PERMISSIONS[normalizedRoute]) {
    return ROUTE_PERMISSIONS[normalizedRoute];
  }

  // Check wildcard patterns
  for (const [pattern, permissions] of Object.entries(ROUTE_PERMISSIONS)) {
    if (pattern.includes('*')) {
      const regex = new RegExp('^' + pattern.replace(/\*/g, '.*') + '$');
      if (regex.test(normalizedRoute)) {
        return permissions;
      }
    }
  }

  // Check prefix patterns for spare_parts and light routes (must be checked before other patterns)
  // IMPORTANT: Order matters - check specific patterns before generic ones
  if (normalizedRoute.startsWith('/spare_parts_')) {
    return [PERMISSIONS.SparePartsView];
  }
  if (normalizedRoute.startsWith('/su_light_')) {
    return [PERMISSIONS.LightView];
  }
  // Check for pallets route (must be before generic /su_ check)
  if (normalizedRoute === '/su_pallets' || normalizedRoute.startsWith('/su_pallets')) {
    return [PERMISSIONS.PalletsView, PERMISSIONS.PalletsManage];
  }
  // Check for other box routes that might not be explicitly listed
  // But exclude light routes (already handled above) and pallets
  if (normalizedRoute.startsWith('/su_') && 
      !normalizedRoute.startsWith('/su_light_') && 
      normalizedRoute !== '/su_pallets' &&
      (normalizedRoute.includes('box') || normalizedRoute.includes('order') || normalizedRoute.includes('alarm') || normalizedRoute.includes('expiration') || normalizedRoute.includes('archive') || normalizedRoute.includes('dev') || normalizedRoute.includes('fw') || normalizedRoute === '/su_dashboard' || normalizedRoute === '/dashboard')) {
    return [PERMISSIONS.BoxView];
  }
  if (normalizedRoute.startsWith('/cu_')) {
    return [PERMISSIONS.BoxView];
  }

  // Default: no specific permission required (but user must be authenticated)
  // Only allow access to explicitly public routes
  const publicRoutes = ['/pe_landing', '/account/login'];
  if (publicRoutes.includes(normalizedRoute)) {
    return [];
  }
  
  // For unknown routes, require authentication but no specific permission
  // This is safer than allowing all routes
  return [];
}

/**
 * Check if a route requires any permissions
 */
export function routeRequiresPermissions(route: string): boolean {
  const permissions = getRequiredPermissions(route);
  return permissions.length > 0;
}
